import React, { useState, useEffect } from 'react';
import { Flex, Text } from '@mantine/core';
import { format, addMinutes, parseISO } from 'date-fns';
import CompactCalendarButton from 'components/Calendar/CompactCalendarButton';
import useExternalApiService from 'helpers/ExternalApiService';
import styles from './RouteCalendarExport.module.scss';

interface Waypoint {
  travelTime?: number;
  id?: number;
  address?: string;
  latitude?: string;
  longitude?: string;
  isFirstLocation?: boolean;
  isLastLocation?: boolean;
  start_time?: string;  // Format: "HH:MM AM/PM" or "YYYY-MM-DD HH:MM:SS"
  duration?: string | number;    // Can be "15 min" or "1 hour" or "30" (string) or 30 (number)
  end_time?: string;
  arriveTime?: string;
  name?: string;
}

interface RouteCalendarExportProps {
  routeDate: string | null;
  meetings: (Waypoint | null)[];
}

const RouteCalendarExport: React.FC<RouteCalendarExportProps> = ({ routeDate, meetings }) => {
  const { postData } = useExternalApiService(process.env.REACT_APP_EAI_URL);
  const [calendarAddedMap, setCalendarAddedMap] = useState<Map<number, boolean>>(new Map());

  // Filter meetings that have start times and are not start/end locations
  const meetingsWithTimes = meetings.filter(
    (m): m is Waypoint =>
      m !== null &&
      m.start_time !== undefined &&
      m.start_time !== "" &&
      m.start_time !== null &&
      m.start_time !== "null" &&
      !m.isFirstLocation &&
      !m.isLastLocation
  );

  // Load calendar states from backend on mount
  useEffect(() => {
    const loadCalendarStates = async () => {
      if (!routeDate || meetingsWithTimes.length === 0) return;

      for (const meeting of meetingsWithTimes) {
        if (!meeting.id) continue;

        const startTime24h = convert12HourTo24Hour(meeting.start_time || '');
        const followUpDateTime = `${routeDate} ${startTime24h}:00`;

        try {
          const response = await postData('/api/e/call_get_calendar_confirmation', {
            practitioner_id: meeting.id,
            follow_up_date: followUpDateTime
          });

          if (response?.calendar_added) {
            setCalendarAddedMap(prev => new Map(prev).set(meeting.id!, true));
          }
        } catch (error) {
          console.error('Error loading calendar state for meeting:', error);
        }
      }
    };

    loadCalendarStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDate, meetingsWithTimes.length]);

  // Don't show if no date or no meetings with times
  if (!routeDate || meetingsWithTimes.length === 0) {
    return null;
  }

  // Helper to convert 12-hour time to 24-hour format
  const convert12HourTo24Hour = (time12h: string): string => {
    if (!time12h || time12h.trim() === '') return '';

    // If already in HH:MM format (24-hour), return as is
    if (/^\d{2}:\d{2}$/.test(time12h)) {
      return time12h;
    }

    // If in ISO datetime format "YYYY-MM-DDTHH:MM:SS", extract time
    if (time12h.includes('T') && time12h.includes(':')) {
      const timePart = time12h.split('T')[1];
      if (timePart) {
        return timePart.substring(0, 5); // HH:MM
      }
    }

    // If in datetime format "YYYY-MM-DD HH:MM:SS" (with space separator), extract time
    if (time12h.includes('-') && time12h.includes(':') && time12h.includes(' ')) {
      const timePart = time12h.split(' ')[1];
      if (timePart) {
        return timePart.substring(0, 5); // HH:MM
      }
    }

    // Handle 12-hour format "HH:MM AM/PM"
    const parts = time12h.split(' ');
    if (parts.length < 2) {
      console.error('Invalid time format (no AM/PM):', time12h);
      return ''; // Invalid format - no AM/PM marker
    }

    const [time, period] = parts;
    if (!time || !time.includes(':')) {
      console.error('Invalid time format (no colon):', time12h);
      return '';
    }

    const timeParts = time.split(':');
    const hours = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);

    if (isNaN(hours) || isNaN(minutes)) {
      console.error('Invalid hours/minutes:', { time12h, hours, minutes });
      return '';
    }

    const isPM = period === 'PM';
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && period === 'AM' ? 0 : hours);
    return `${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  // Helper to parse duration string to minutes
  const parseDuration = (durationStr: string | number | undefined): number => {
    if (!durationStr) return 30; // Default 30 minutes

    // If it's already a number, return it directly
    if (typeof durationStr === 'number') {
      return durationStr;
    }

    // Convert to string for parsing
    const str = String(durationStr);

    // If it's just a number, treat as minutes
    const numericMatch = str.match(/^\d+$/);
    if (numericMatch) {
      return parseInt(str);
    }

    // Parse "15 min" or "1 hour" format
    const minutes = str.match(/(\d+)\s*min/);
    if (minutes) {
      return parseInt(minutes[1]);
    }

    const hours = str.match(/(\d+(?:\.\d+)?)\s*hour/);
    if (hours) {
      return Math.round(parseFloat(hours[1]) * 60);
    }

    return 30; // Default if can't parse
  };

  // Helper to calculate end time from start time and duration
  const calculateEndTime = (startTime: string, durationStr: string | number | undefined): string => {
    if (!routeDate) return '00:00';
    const time24h = convert12HourTo24Hour(startTime);

    // Defensive check: ensure we have a valid time before parsing
    if (!time24h) {
      console.error('Invalid time24h:', { startTime, time24h });
      return '00:00';
    }

    const durationMinutes = parseDuration(durationStr);

    try {
      const startDateTime = parseISO(`${routeDate}T${time24h}:00`);
      const endDateTime = addMinutes(startDateTime, durationMinutes);
      return format(endDateTime, 'HH:mm');
    } catch (error) {
      console.error('Failed to parse date:', { routeDate, time24h, error });
      return '00:00';
    }
  };

  const routeDateStr = routeDate || '';

  // Handler for calendar export callback
  const handleCalendarAdded = async (meetingId: number, startTime: string, provider: string) => {
    const startTime24h = convert12HourTo24Hour(startTime);
    const followUpDateTime = `${routeDateStr} ${startTime24h}:00`;

    try {
      await postData('/api/e/call_save_calendar_confirmation', {
        practitioner_id: meetingId,
        follow_up_date: followUpDateTime,
        meeting_source: 'Route'  // Mark as Route meeting
      });

      setCalendarAddedMap(prev => new Map(prev).set(meetingId, true));
    } catch (error) {
      console.error('Error saving calendar confirmation:', error);
    }
  };

  return (
    <Flex direction="column" gap={12} mt={20} mb={20}>
      <Text size="13px" fw={600} c="#4F7D9C">
        Export Meetings to Calendar ({meetingsWithTimes.length} meeting{meetingsWithTimes.length > 1 ? 's' : ''} with set time)
      </Text>

      <Text size="11px" c="#8B8B8B" mt={-8}>
        💡 Click any button to add that meeting to your calendar
      </Text>

      {meetingsWithTimes.map((meeting, index) => {
        const startTime24h = convert12HourTo24Hour(meeting.start_time || '');

        // Defensive check: validate data BEFORE calling calculateEndTime
        if (!startTime24h || !routeDateStr) {
          console.error('Skipping meeting (invalid data):', {
            name: meeting.name,
            startTime24h,
            routeDateStr,
            meeting
          });
          return null;
        }

        const endTime = calculateEndTime(meeting.start_time || '', meeting.duration);

        const dateTimeStr = `${routeDateStr}T${startTime24h}:00`;
        let displayTime: string;
        try {
          displayTime = format(parseISO(dateTimeStr), 'h:mm a');
        } catch (error) {
          console.error('Failed to parse date:', { dateTimeStr, error });
          return null;
        }

        const meetingId = meeting.id || 0;
        const isAdded = calendarAddedMap.get(meetingId) || false;

        return (
          <Flex key={meetingId || index} align="center" gap={12} className={styles.meetingRow}>
            <Text size="12px" c="#666" style={{ minWidth: '60px' }}>
              {displayTime}
            </Text>

            <div style={{ flex: 1 }}>
              <CompactCalendarButton
                meetingDetails={{
                  name: `Visit: ${meeting.name || 'Practitioner'}`,
                  startDate: routeDateStr,
                  startTime: startTime24h,
                  endTime: endTime,
                  timeZone: 'America/New_York',
                  location: meeting.address || '',
                  description: `Meeting ${index + 1} on route`
                }}
                isCalendarAdded={isAdded}
                onCalendarAdded={async (provider) => {
                  await handleCalendarAdded(meetingId, meeting.start_time || '', provider);
                }}
              />
            </div>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default RouteCalendarExport;
